import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useBusinessContext } from '@context'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@hooks'
import {
  fetchPurchaseInvoiceImportStatus,
  IPurchaseInvoiceImportErrorPayload,
  PurchaseInvoiceImportStatus
} from '@root/query'
import { getInvoicingPageUrl } from '@root/constants'
import toast from 'react-hot-toast'
import { resolveImportErrorMessage } from '../purchaseInvoiceImportCopy'
import { isGermanBusinessCountry } from '../xrechnungDelivery'
import styled from 'styled-components'

interface ActivePurchaseImport {
  jobId: string
  purchaseInvoiceId: number | null
}

interface StartPurchaseInvoiceImportTrackingPayload {
  jobId: string
  purchaseInvoiceId: number | null
}

const inProgressStatuses: PurchaseInvoiceImportStatus[] = ['upload_started', 'analyzing']

export const usePurchaseInvoiceImportTracker = () => {
  const { slug, data: business } = useBusinessContext()
  const isGermanBusiness = isGermanBusinessCountry(business?.country)
  const history = useHistory()
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const theme = useTheme()
  const [activePurchaseImport, setActivePurchaseImport] = useState<ActivePurchaseImport | null>(
    null
  )
  const lastToastStatusRef = useRef<PurchaseInvoiceImportStatus | null>(null)
  const toastStyle = useMemo(
    () => ({
      borderRadius: `${theme.borderRadius.md}rem`,
      background: theme.colors.menuBg,
      color: theme.colors.neutralWhite
    }),
    [theme.borderRadius.md, theme.colors.menuBg, theme.colors.neutralWhite]
  )

  const purchaseImportToastId = activePurchaseImport
    ? `purchase-invoice-import-${activePurchaseImport.jobId}`
    : null

  const getInProgressToastMessage = (status: PurchaseInvoiceImportStatus) => (
    <ProgressToastContent>
      <p>{t(`invoicing.purchaseInvoiceImport.status.${status}`)}</p>
      <p>{t('invoicing.purchaseInvoiceImport.status.backgroundHint')}</p>
    </ProgressToastContent>
  )

  const startTracking = ({
    jobId,
    purchaseInvoiceId
  }: StartPurchaseInvoiceImportTrackingPayload) => {
    setActivePurchaseImport({ jobId, purchaseInvoiceId })
    toast.loading(getInProgressToastMessage('upload_started'), {
      id: `purchase-invoice-import-${jobId}`,
      style: toastStyle
    })
  }

  const { data: purchaseImportStatus, error: purchaseImportStatusError } = useQuery({
    queryKey: [slug, 'purchase-invoice-import-status', activePurchaseImport?.jobId],
    queryFn: () =>
      fetchPurchaseInvoiceImportStatus({ slug, jobId: activePurchaseImport?.jobId as string }),
    enabled: Boolean(activePurchaseImport?.jobId),
    refetchInterval: query => {
      const status = query.state.data?.status
      if (!status) return 2000
      return status === 'completed' || status === 'failed' ? false : 2000
    }
  })

  useEffect(() => {
    if (!purchaseImportStatus || !purchaseImportToastId) return
    if (lastToastStatusRef.current === purchaseImportStatus.status) return

    lastToastStatusRef.current = purchaseImportStatus.status

    if (purchaseImportStatus.status === 'failed') {
      toast.error(
        resolveImportErrorMessage(
          {
            error_code: purchaseImportStatus.error_code,
            error_message: purchaseImportStatus.error_message
          },
          isGermanBusiness,
          t
        ),
        { id: purchaseImportToastId, style: toastStyle }
      )
      setActivePurchaseImport(null)
      return
    }

    if (purchaseImportStatus.status === 'completed') {
      const purchaseInvoiceId =
        purchaseImportStatus.purchase_invoice_id ?? activePurchaseImport?.purchaseInvoiceId

      toast.success(t('invoicing.purchaseInvoiceImport.status.completed'), {
        id: purchaseImportToastId,
        style: toastStyle
      })
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: [slug, 'purchase_invoices'] }),
        queryClient.invalidateQueries({ queryKey: [slug, 'combined_invoices'] })
      ]).then(() => {
        if (purchaseInvoiceId) {
          history.push(getInvoicingPageUrl(slug, { id: purchaseInvoiceId, type: 'purchase' }))
        }
      })
      setActivePurchaseImport(null)
      return
    }

    if (inProgressStatuses.includes(purchaseImportStatus.status)) {
      toast.loading(getInProgressToastMessage(purchaseImportStatus.status), {
        id: purchaseImportToastId,
        style: toastStyle
      })
    }
  }, [
    activePurchaseImport?.purchaseInvoiceId,
    history,
    isGermanBusiness,
    purchaseImportStatus,
    purchaseImportToastId,
    queryClient,
    slug,
    t,
    toastStyle
  ])

  useEffect(() => {
    if (!purchaseImportStatusError || !purchaseImportToastId) return

    const payload = (purchaseImportStatusError as { data?: IPurchaseInvoiceImportErrorPayload })
      ?.data
    toast.error(
      payload?.error_code
        ? resolveImportErrorMessage(payload, isGermanBusiness, t)
        : payload?.error_message || t('invoicing.purchaseInvoiceImport.error.importJobNotFound'),
      { id: purchaseImportToastId, style: toastStyle }
    )
    setActivePurchaseImport(null)
  }, [isGermanBusiness, purchaseImportStatusError, purchaseImportToastId, t, toastStyle])

  useEffect(() => {
    if (activePurchaseImport) return
    lastToastStatusRef.current = null
  }, [activePurchaseImport])

  return { startTracking }
}

const ProgressToastContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;

  & > p {
    margin: 0;
  }
`
